import { useEffect, useRef, useState } from 'react';

/**
 * Delay-then-hold for a loading skeleton. Ported from `apps/platform/src/hooks/useDeferredLoading.ts`.
 *
 * ## Why this exists in `apps/app` at all
 *
 * The app's skeletons are already footprint-exact (`CertificatePageSkeleton`,
 * `ProvenanceListSkeleton`, `ScansListSkeleton`, `ActivityListSkeleton` all mirror
 * the loaded layout). What was wrong was their DURATION. Measured at 1280 against
 * the local API, before this hook:
 *
 *   certificates -> a certificate   23 skeletons, visible  60.0ms  (119.2ms on a colder run)
 *   certificate  -> Provenance      15 skeletons, visible 220.5ms
 *   certificate  -> Scans (map)      0 skeletons, nothing at all
 *
 * A placeholder that appears and disappears inside ~60ms is not a loading state, it
 * is a flicker — and it is why the page change read as "no transition": the eye
 * catches the appearance and disappearance, not the content. The platform hit the
 * identical symptom on its support list and answered it with exactly this hook, so
 * this is the same mechanism rather than a second invention.
 *
 * ## The §36 note
 *
 * This IS the second copy — the platform owns the original. It is duplicated rather
 * than hoisted into `@repo/ui` deliberately and temporarily: hoisting means editing a
 * shared package that `apps/platform` imports from, and both apps are under concurrent
 * edit. The right home is `@repo/ui` (it is pure logic, no theme, so the 4px/8px
 * spacing trap in §47 does not apply to it). Moving it there, and pointing the
 * platform's five call sites at the shared copy, is a follow-up — not something to do
 * from inside an app-scoped change.
 *
 * Keep the two files' CONSTANTS in lockstep if either is tuned; they are one decision.
 */

/**
 * How long a query gets to resolve before its skeleton is allowed on screen.
 *
 * 180ms, matching the platform. Against the measurements above it is the number that
 * makes the difference: a 60ms response now shows NOTHING (correct — there was never
 * anything to wait for), and a 220ms one gets a display long enough to read.
 */
export const DEFERRED_LOADING_DELAY_MS = 180;

/**
 * How long the skeleton stays once it HAS been shown, even if the data lands immediately after.
 *
 * The delay alone is not enough: a response arriving at 190ms clears the delay by 10ms,
 * so the skeleton mounts and is torn down a frame later — the same blink, moved. A floor
 * on the visible duration closes that window.
 */
export const DEFERRED_LOADING_MIN_DISPLAY_MS = 320;

/**
 * How long the loaded content takes to fade in.
 *
 * Legitimate ONLY because the skeleton and the loaded content occupy identical
 * footprints — polish on a stable layout, not a mask for movement. Strictly OPACITY:
 * no transform, no height or margin animation.
 *
 * A transform here would also be actively harmful in this app specifically: it becomes
 * the containing block for every `position: fixed` descendant, and `StickyActionBar`
 * is exactly that. That is the same reason `NavigationHeader` is `sticky` rather than
 * `fixed`, and it is why the deleted page fade was opacity-only too.
 */
export const DEFERRED_LOADING_FADE_MS = 120;

interface UseDeferredLoadingOptions {
  /** Grace period before the skeleton is allowed to mount. */
  delay?: number;
  /** Floor on how long it stays once it has. */
  minDisplay?: number;
}

/**
 * Should a loading skeleton be on screen right now?
 *
 * ## The contract
 *
 * | `isLoading` goes | within | result |
 * |---|---|---|
 * | true -> false | < `delay` | never returns true — the skeleton is never mounted |
 * | true -> false | > `delay` | true for at least `minDisplay` from when it first went true |
 * | false at mount | — | false immediately (cached data: no delay, no flash) |
 * | true -> false -> true | any | the pending timer is dropped, not left to fire late |
 *
 * ## Using it
 *
 * The caller gets a THIRD state and has to render it deliberately:
 *
 * ```tsx
 * const showSkeleton = useDeferredLoading(isLoading);
 * ...
 * {showSkeleton ? <Skeleton /> : isLoading ? null : <Content />}
 * ```
 *
 * The `isLoading ? null` arm is NOT optional. Falling straight through to the loaded
 * branch during the grace period renders the page's EMPTY state over data still in
 * flight — "No scans yet" on a certificate that has fourteen — which is a worse
 * artifact than the flash. Rendering nothing for <180ms is imperceptible; rendering
 * the wrong answer is not.
 */
export const useDeferredLoading = (
  isLoading: boolean,
  {
    delay = DEFERRED_LOADING_DELAY_MS,
    minDisplay = DEFERRED_LOADING_MIN_DISPLAY_MS,
  }: UseDeferredLoadingOptions = {},
): boolean => {
  const [visible, setVisible] = useState(false);

  /**
   * When the skeleton went up, for measuring `minDisplay` against.
   *
   * Set inside the show timer rather than at the start of the delay, so the floor covers
   * the time it is actually ON SCREEN. Measuring from the start of the delay would let a
   * response landing at `delay + 1` hide it 179ms early — reintroducing the blink the
   * floor exists to prevent.
   */
  const shownAtRef = useRef(0);

  useEffect(() => {
    if (isLoading) {
      // Already up. Do NOT restart the clock: a query that flickers back to loading (a
      // refetch, a filter change) while the skeleton is still showing should extend one
      // continuous display, not begin a second that outlives it by another minDisplay.
      if (visible) return;

      const timer = setTimeout(() => {
        shownAtRef.current = Date.now();
        setVisible(true);
      }, delay);
      // Cleanup covers three cases with one line: unmount, `isLoading` flipping back to
      // false inside the grace period (the fast-response case — the timer is dropped and
      // nothing is ever shown), and a true -> false -> true bounce, which lands here
      // before the next run schedules a fresh timer. A `setTimeout` surviving unmount
      // would call `setVisible` on a dead component.
      return () => clearTimeout(timer);
    }

    // Resolved inside the grace period. Nothing was mounted, so there is nothing to hold.
    if (!visible) return;

    const remaining = minDisplay - (Date.now() - shownAtRef.current);
    if (remaining <= 0) {
      // The floor is already satisfied — hand over on this render rather than scheduling
      // a zero-length timeout, which would cost a frame for nothing.
      setVisible(false);
      return;
    }

    const timer = setTimeout(() => setVisible(false), remaining);
    return () => clearTimeout(timer);
    // `visible` is a dependency on purpose: it is what re-runs this effect when the show
    // timer fires, so the hide path is evaluated against the state the timer just set
    // rather than a stale closure.
  }, [isLoading, visible, delay, minDisplay]);

  return visible;
};
